import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ArrowDown,
  ArrowUp,
  Copy,
  Image as ImageIcon,
  Layers,
  Minus,
  Plus,
  Square,
  Trash2,
  Type,
  Barcode,
  PanelTop,
  Link2,
  Table2,
  Grid3x3,
} from 'lucide-react';
import { LABEL_CANVAS_FONTS, fontFamilyForId, fontIdForFamily } from '../labelCanvasFonts';
import { snapElementBounds } from '../labelCanvasSnap';
import type { LabelCanvasWrapGuide } from '../productLabelTypes';
import type { NutritionTableRow } from '../labelCanvasTypes';
import { DEFAULT_NUTRITION_ROWS } from '../labelCanvasTypes';
import type { ProductLabelDesign, ProductLabelLayout, ProductWorkspaceDetail } from '../productLabelTypes';
import { inchesToPx, mergeLabelLayout } from '../productLabelTypes';
import { LABEL_FIELD_DEFS } from '../labelFieldRegistry';
import type { LabelFieldKey } from '../labelLayoutEngine';
import {
  createCanvasElement,
  duplicateElement,
  mergeCanvasElements,
  moveElement,
  nextZIndex,
  removeElement,
  reorderLayer,
  updateElement,
  type LabelCanvasElement,
  type LabelCanvasElementType,
} from '../labelCanvasTypes';
import { clonePresetElements, LABEL_CANVAS_PRESETS } from '../labelCanvasPresets';
import { LabelCanvasSurface } from '../labelCanvasRenderer';
import type { LabelCanvasRenderContext } from '../labelCanvasRenderer';

type Props = {
  product: Pick<
    ProductWorkspaceDetail,
    | 'sku'
    | 'name'
    | 'description'
    | 'benefits'
    | 'imageUrl'
    | 'unitLabel'
    | 'baseUnit'
    | 'salePrice'
    | 'pricePerUnit'
    | 'hsCode'
    | 'countryOfOrigin'
    | 'typicalExpiryDays'
    | 'category'
  >;
  design: Pick<
    ProductLabelDesign,
    | 'widthIn'
    | 'heightIn'
    | 'layout'
    | 'packageWeightLbs'
    | 'packageLengthIn'
    | 'packageWidthIn'
    | 'packageHeightIn'
    | 'packageMaterial'
  >;
  netQuantityLabel?: string | null;
  formatPrice: (n: number) => string;
  currency: string;
  printId?: string;
  onLayoutChange: (patch: Partial<ProductLabelLayout>) => void;
  onDesignChange?: (patch: Partial<Pick<ProductLabelDesign, 'widthIn' | 'heightIn'>>) => void;
  onUploadImage?: (file: File) => Promise<string | null>;
};

type DragMode =
  | { kind: 'move'; id: string; startX: number; startY: number; origX: number; origY: number }
  | { kind: 'resize'; id: string; startX: number; startY: number; origW: number; origH: number };

const TOOL_BUTTONS: Array<{ type: LabelCanvasElementType; label: string; icon: React.ReactNode }> = [
  { type: 'text', label: 'Text', icon: <Type className="h-3.5 w-3.5" /> },
  { type: 'boundField', label: 'Product data', icon: <Link2 className="h-3.5 w-3.5" /> },
  { type: 'image', label: 'Image', icon: <ImageIcon className="h-3.5 w-3.5" /> },
  { type: 'shape', label: 'Shape', icon: <Square className="h-3.5 w-3.5" /> },
  { type: 'panel', label: 'Panel', icon: <PanelTop className="h-3.5 w-3.5" /> },
  { type: 'barcode', label: 'Barcode', icon: <Barcode className="h-3.5 w-3.5" /> },
  { type: 'nutritionTable', label: 'Nutrition', icon: <Table2 className="h-3.5 w-3.5" /> },
];

export default function LabelCanvasStudio({
  product,
  design,
  netQuantityLabel,
  formatPrice,
  currency,
  printId = 'product-label-print-root',
  onLayoutChange,
  onDesignChange,
  onUploadImage,
}: Props) {
  const layout = mergeLabelLayout(design.layout);
  const elements = mergeCanvasElements(layout.canvasElements);
  const elementsRef = useRef(elements);
  elementsRef.current = elements;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const dragRef = useRef<DragMode | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  const widthPx = inchesToPx(design.widthIn);
  const heightPx = inchesToPx(design.heightIn);
  const isPortrait = layout.orientation === 'portrait';
  const labelW = isPortrait ? heightPx : widthPx;
  const labelH = isPortrait ? widthPx : heightPx;

  const renderCtx: LabelCanvasRenderContext = useMemo(
    () => ({
      product,
      layout,
      design,
      netQuantityLabel,
      formatPrice,
      currency,
      labelW,
      labelH,
    }),
    [product, layout, design, netQuantityLabel, formatPrice, currency, labelW, labelH],
  );

  const selected = elements.find((e) => e.id === selectedId) ?? null;

  const setElements = useCallback(
    (next: LabelCanvasElement[] | ((prev: LabelCanvasElement[]) => LabelCanvasElement[])) => {
      const resolved = typeof next === 'function' ? next(elementsRef.current) : next;
      elementsRef.current = resolved;
      onLayoutChange({
        canvasElements: resolved,
        layoutTemplate: 'CANVAS',
      });
    },
    [onLayoutChange],
  );

  const addElement = (type: LabelCanvasElementType) => {
    const el = createCanvasElement(type, { zIndex: nextZIndex(elements) });
    setElements([...elements, el]);
    setSelectedId(el.id);
  };

  const applyPreset = (presetId: string) => {
    const preset = LABEL_CANVAS_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    onLayoutChange({
      layoutTemplate: 'CANVAS',
      canvasElements: clonePresetElements(preset),
      backgroundColor: preset.backgroundColor,
      accentColor: preset.accentColor,
      textColor: preset.textColor,
      canvasWrapGuide: preset.wrapGuide ?? 'none',
    });
    onDesignChange?.({ widthIn: preset.widthIn, heightIn: preset.heightIn });
  };

  const fitZoom = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const pad = 48;
    const scaleX = (vp.clientWidth - pad) / labelW;
    const scaleY = (vp.clientHeight - pad) / labelH;
    setZoom(Math.min(1.5, Math.max(0.35, Math.min(scaleX, scaleY))));
  }, [labelW, labelH]);

  useEffect(() => {
    fitZoom();
    window.addEventListener('resize', fitZoom);
    return () => window.removeEventListener('resize', fitZoom);
  }, [fitZoom, design.widthIn, design.heightIn]);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const scale = zoom;
      const dxPct = ((e.clientX - drag.startX) / scale / labelW) * 100;
      const dyPct = ((e.clientY - drag.startY) / scale / labelH) * 100;
      const snapCfg = layoutRef.current;
      const snapOn = snapCfg.canvasSnapEnabled;
      const grid = snapCfg.canvasSnapGridPct;
      if (drag.kind === 'move') {
        setElements((prev) =>
          moveElement(
            prev,
            drag.id,
            snapElementBounds(
              {
                x: Math.min(100, Math.max(0, drag.origX + dxPct)),
                y: Math.min(100, Math.max(0, drag.origY + dyPct)),
              },
              grid,
              snapOn,
            ),
          ),
        );
      } else {
        setElements((prev) =>
          moveElement(
            prev,
            drag.id,
            snapElementBounds(
              {
                w: Math.min(100, Math.max(4, drag.origW + dxPct)),
                h: Math.min(100, Math.max(2, drag.origH + dyPct)),
              },
              grid,
              snapOn,
            ),
          ),
        );
      }
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [labelH, labelW, setElements, zoom]);

  const startMove = (id: string, e: React.PointerEvent) => {
    const el = elements.find((x) => x.id === id);
    if (!el || el.locked) return;
    dragRef.current = {
      kind: 'move',
      id,
      startX: e.clientX,
      startY: e.clientY,
      origX: el.x,
      origY: el.y,
    };
  };

  const startResize = (id: string, e: React.PointerEvent) => {
    e.stopPropagation();
    const el = elements.find((x) => x.id === id);
    if (!el || el.locked) return;
    dragRef.current = {
      kind: 'resize',
      id,
      startX: e.clientX,
      startY: e.clientY,
      origW: el.w,
      origH: el.h,
    };
  };

  const patchSelected = (patch: Partial<LabelCanvasElement>) => {
    if (!selectedId) return;
    setElements(updateElement(elements, selectedId, patch));
  };

  return (
    <div className="flex h-full min-h-[28rem] w-full flex-col bg-slate-100">
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-white px-2 py-1.5">
        {TOOL_BUTTONS.map((tool) => (
          <button
            key={tool.type}
            type="button"
            title={`Add ${tool.label}`}
            onClick={() => addElement(tool.type)}
            className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-[11px] font-medium text-slate-700 hover:border-sky-500 hover:bg-sky-50"
          >
            {tool.icon}
            {tool.label}
          </button>
        ))}
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <button
          type="button"
          onClick={() => setZoom((z) => Math.min(2, z + 0.1))}
          className="rounded border border-slate-200 p-1 hover:bg-slate-50"
          title="Zoom in"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => setZoom((z) => Math.max(0.25, z - 0.1))}
          className="rounded border border-slate-200 p-1 hover:bg-slate-50"
          title="Zoom out"
        >
          <Minus className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={fitZoom}
          className="rounded border border-slate-200 px-2 py-1 text-[10px] font-medium text-slate-600 hover:bg-slate-50"
        >
          Fit
        </button>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <button
          type="button"
          title="Snap to grid"
          onClick={() => onLayoutChange({ canvasSnapEnabled: !layout.canvasSnapEnabled })}
          className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[10px] font-medium ${
            layout.canvasSnapEnabled
              ? 'border-sky-500 bg-sky-50 text-sky-800'
              : 'border-slate-200 text-slate-600'
          }`}
        >
          <Grid3x3 className="h-3.5 w-3.5" />
          Snap
        </button>
        <select
          title="Wrap-around panel guides"
          value={layout.canvasWrapGuide}
          onChange={(e) =>
            onLayoutChange({ canvasWrapGuide: e.target.value as LabelCanvasWrapGuide })
          }
          className="max-w-[7rem] rounded border border-slate-200 px-1.5 py-1 text-[10px] text-slate-700"
        >
          <option value="none">No wrap guide</option>
          <option value="2-panel">2-panel wrap</option>
          <option value="3-panel">3-panel wrap</option>
        </select>
        <span className="ml-auto text-[10px] text-slate-500">
          {design.widthIn}″ × {design.heightIn}″ · drag to move · corner to resize
        </span>
      </div>

      <div className="flex min-h-0 flex-1">
        <aside className="flex w-36 shrink-0 flex-col border-r border-slate-200 bg-white">
          <div className="flex items-center gap-1 border-b border-slate-100 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
            <Layers className="h-3 w-3" />
            Layers
          </div>
          <div className="flex-1 overflow-y-auto p-1">
            {[...elements]
              .sort((a, b) => b.zIndex - a.zIndex)
              .map((el) => (
                <button
                  key={el.id}
                  type="button"
                  onClick={() => setSelectedId(el.id)}
                  className={`mb-0.5 flex w-full items-center gap-1 rounded px-1.5 py-1 text-left text-[10px] ${
                    selectedId === el.id ? 'bg-sky-100 text-sky-900' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <span className="truncate capitalize">{el.type}</span>
                  {el.text ? (
                    <span className="truncate text-slate-400">· {el.text.slice(0, 12)}</span>
                  ) : null}
                </button>
              ))}
          </div>
        </aside>

        <div
          ref={viewportRef}
          className="relative flex flex-1 items-center justify-center overflow-auto bg-gradient-to-br from-slate-200/90 to-slate-300/70 p-6"
        >
          <div
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: 'center center',
            }}
          >
            <div className="relative">
              <LabelCanvasSurface
                ctx={renderCtx}
                elements={elements}
                editable
                selectedId={selectedId}
                printId={printId}
                showGrid={layout.canvasSnapEnabled}
                showWrapGuide
                onSelect={setSelectedId}
                onElementPointerDown={startMove}
                onCanvasPointerDown={() => setSelectedId(null)}
              />
              {selected && !selected.locked ? (
                <div
                  data-label-editor-chrome
                  className="pointer-events-none absolute border-2 border-sky-500"
                  style={{
                    left: `${selected.x}%`,
                    top: `${selected.y}%`,
                    width: `${selected.w}%`,
                    height: `${selected.h}%`,
                    zIndex: 9999,
                  }}
                >
                  <div
                    data-label-editor-chrome
                    className="pointer-events-auto absolute -bottom-1 -right-1 h-3 w-3 cursor-se-resize rounded-sm border border-white bg-sky-500"
                    onPointerDown={(e) => startResize(selected.id, e)}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <aside className="flex w-48 shrink-0 flex-col overflow-y-auto border-l border-slate-200 bg-white p-2">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-500">Properties</p>
          {!selected ? (
            <div className="space-y-2 text-[11px] text-slate-600">
              <p>Select an element on the canvas, or adjust studio settings below.</p>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={layout.canvasSnapEnabled}
                  onChange={(e) => onLayoutChange({ canvasSnapEnabled: e.target.checked })}
                />
                Snap to grid
              </label>
              <label className="block">
                Grid size ({layout.canvasSnapGridPct}%)
                <input
                  type="range"
                  min="2"
                  max="20"
                  step="1"
                  className="w-full"
                  value={layout.canvasSnapGridPct}
                  onChange={(e) =>
                    onLayoutChange({ canvasSnapGridPct: Number(e.target.value) })
                  }
                />
              </label>
              <label className="block">
                Wrap guide
                <select
                  className="mt-0.5 w-full border border-slate-300 px-1 py-1"
                  value={layout.canvasWrapGuide}
                  onChange={(e) =>
                    onLayoutChange({ canvasWrapGuide: e.target.value as LabelCanvasWrapGuide })
                  }
                >
                  <option value="none">None</option>
                  <option value="2-panel">2-panel (front/back)</option>
                  <option value="3-panel">3-panel (wrap)</option>
                </select>
              </label>
            </div>
          ) : (
            <div className="space-y-2 text-[11px]">
              <div className="flex gap-1">
                <button
                  type="button"
                  title="Duplicate"
                  onClick={() => {
                    setElements(duplicateElement(elements, selected.id));
                  }}
                  className="rounded border border-slate-200 p-1 hover:bg-slate-50"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  title="Bring forward"
                  onClick={() => setElements(reorderLayer(elements, selected.id, 'up'))}
                  className="rounded border border-slate-200 p-1 hover:bg-slate-50"
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  title="Send back"
                  onClick={() => setElements(reorderLayer(elements, selected.id, 'down'))}
                  className="rounded border border-slate-200 p-1 hover:bg-slate-50"
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  title="Delete"
                  onClick={() => {
                    setElements(removeElement(elements, selected.id));
                    setSelectedId(null);
                  }}
                  className="rounded border border-rose-200 p-1 text-rose-600 hover:bg-rose-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>

              {(selected.type === 'text' || selected.type === 'panel') && (
                <label className="block">
                  Text
                  <textarea
                    className="mt-0.5 w-full border border-slate-300 px-1.5 py-1 text-[11px]"
                    rows={3}
                    value={selected.text || ''}
                    onChange={(e) => patchSelected({ text: e.target.value })}
                  />
                </label>
              )}

              {selected.type === 'panel' && (
                <label className="block">
                  Panel title
                  <input
                    className="mt-0.5 w-full border border-slate-300 px-1.5 py-1"
                    value={selected.panelTitle || ''}
                    onChange={(e) => patchSelected({ panelTitle: e.target.value })}
                  />
                </label>
              )}

              {selected.type === 'boundField' && (
                <label className="block">
                  Product field
                  <select
                    className="mt-0.5 w-full border border-slate-300 px-1 py-1"
                    value={selected.fieldKey || 'name'}
                    onChange={(e) =>
                      patchSelected({ fieldKey: e.target.value as LabelFieldKey })
                    }
                  >
                    {LABEL_FIELD_DEFS.map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {selected.type === 'image' && (
                <>
                  <label className="block">
                    Image URL
                    <input
                      className="mt-0.5 w-full border border-slate-300 px-1.5 py-1"
                      value={selected.imageUrl || ''}
                      onChange={(e) => patchSelected({ imageUrl: e.target.value })}
                    />
                  </label>
                  {onUploadImage ? (
                    <label className="block cursor-pointer text-sky-700 underline">
                      Upload image
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          void onUploadImage(f).then((url) => {
                            if (url) patchSelected({ imageUrl: url });
                          });
                          e.target.value = '';
                        }}
                      />
                    </label>
                  ) : null}
                </>
              )}

              {(selected.type === 'text' ||
                selected.type === 'boundField' ||
                selected.type === 'panel' ||
                selected.type === 'nutritionTable') && (
                <>
                  <label className="block">
                    Font
                    <select
                      className="mt-0.5 w-full border border-slate-300 px-1 py-1"
                      value={fontIdForFamily(selected.fontFamily)}
                      onChange={(e) =>
                        patchSelected({ fontFamily: fontFamilyForId(e.target.value) })
                      }
                    >
                      {LABEL_CANVAS_FONTS.map((f) => (
                        <option key={f.id} value={f.id} style={{ fontFamily: f.family }}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selected.fontStyle === 'italic'}
                      onChange={(e) =>
                        patchSelected({ fontStyle: e.target.checked ? 'italic' : 'normal' })
                      }
                    />
                    Italic
                  </label>
                  <label className="block">
                    Font size
                    <input
                      type="range"
                      min="1"
                      max="12"
                      step="0.2"
                      className="w-full"
                      value={selected.fontSizePct ?? 3}
                      onChange={(e) => patchSelected({ fontSizePct: Number(e.target.value) })}
                    />
                  </label>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => patchSelected({ textAlign: 'left' })}
                      className={`rounded border p-1 ${selected.textAlign === 'left' ? 'border-sky-500 bg-sky-50' : 'border-slate-200'}`}
                    >
                      <AlignLeft className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => patchSelected({ textAlign: 'center' })}
                      className={`rounded border p-1 ${selected.textAlign === 'center' ? 'border-sky-500 bg-sky-50' : 'border-slate-200'}`}
                    >
                      <AlignCenter className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => patchSelected({ textAlign: 'right' })}
                      className={`rounded border p-1 ${selected.textAlign === 'right' ? 'border-sky-500 bg-sky-50' : 'border-slate-200'}`}
                    >
                      <AlignRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <label className="block">
                    Text color
                    <input
                      type="color"
                      className="mt-0.5 h-8 w-full border border-slate-300"
                      value={selected.color || layout.textColor}
                      onChange={(e) => patchSelected({ color: e.target.value })}
                    />
                  </label>
                </>
              )}

              {(selected.type === 'shape' || selected.type === 'panel') && (
                <>
                  <label className="block">
                    Fill
                    <input
                      type="color"
                      className="mt-0.5 h-8 w-full border border-slate-300"
                      value={selected.backgroundColor || '#ffffff'}
                      onChange={(e) => patchSelected({ backgroundColor: e.target.value })}
                    />
                  </label>
                  {selected.type === 'shape' && (
                    <label className="block">
                      Shape
                      <select
                        className="mt-0.5 w-full border border-slate-300 px-1 py-1"
                        value={selected.shape || 'rect'}
                        onChange={(e) =>
                          patchSelected({
                            shape: e.target.value as LabelCanvasElement['shape'],
                          })
                        }
                      >
                        <option value="rect">Rectangle</option>
                        <option value="ellipse">Ellipse</option>
                        <option value="semicircle-top">Arch (top)</option>
                        <option value="semicircle-bottom">Arch (bottom)</option>
                        <option value="pill">Pill / capsule</option>
                        <option value="triangle-up">Triangle (up)</option>
                        <option value="triangle-down">Triangle (down)</option>
                        <option value="diamond">Diamond</option>
                        <option value="band-horizontal">Horizontal band</option>
                        <option value="band-vertical">Vertical band</option>
                      </select>
                    </label>
                  )}
                  <label className="block">
                    Border
                    <input
                      type="color"
                      className="mt-0.5 h-8 w-full border border-slate-300"
                      value={selected.borderColor || layout.panelBorderColor}
                      onChange={(e) => patchSelected({ borderColor: e.target.value })}
                    />
                  </label>
                </>
              )}

              {selected.type === 'nutritionTable' && (
                <>
                  <label className="block">
                    Table title
                    <input
                      className="mt-0.5 w-full border border-slate-300 px-1.5 py-1"
                      value={selected.panelTitle || ''}
                      onChange={(e) => patchSelected({ panelTitle: e.target.value })}
                    />
                  </label>
                  <label className="block">
                    Serving note
                    <input
                      className="mt-0.5 w-full border border-slate-300 px-1.5 py-1"
                      value={selected.nutritionServingNote || ''}
                      onChange={(e) => patchSelected({ nutritionServingNote: e.target.value })}
                    />
                  </label>
                  <p className="text-[10px] font-semibold text-slate-500">Nutrients</p>
                  {(selected.nutritionRows ?? DEFAULT_NUTRITION_ROWS).map((row, idx) => (
                    <div key={idx} className="mb-1 flex gap-1">
                      <input
                        className="min-w-0 flex-1 border border-slate-300 px-1 py-0.5"
                        value={row.label}
                        placeholder="Label"
                        onChange={(e) => {
                          const rows = [...(selected.nutritionRows ?? DEFAULT_NUTRITION_ROWS)];
                          rows[idx] = { ...rows[idx], label: e.target.value };
                          patchSelected({ nutritionRows: rows });
                        }}
                      />
                      <input
                        className="w-14 border border-slate-300 px-1 py-0.5"
                        value={row.value}
                        placeholder="Value"
                        onChange={(e) => {
                          const rows = [...(selected.nutritionRows ?? DEFAULT_NUTRITION_ROWS)];
                          rows[idx] = { ...rows[idx], value: e.target.value };
                          patchSelected({ nutritionRows: rows });
                        }}
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    className="text-[10px] font-semibold text-sky-700"
                    onClick={() => {
                      const rows: NutritionTableRow[] = [
                        ...(selected.nutritionRows ?? DEFAULT_NUTRITION_ROWS),
                        { label: 'New nutrient', value: '' },
                      ];
                      patchSelected({ nutritionRows: rows });
                    }}
                  >
                    + Add row
                  </button>
                </>
              )}

              <label className="block">
                Rotation °
                <input
                  type="number"
                  className="mt-0.5 w-full border border-slate-300 px-1.5 py-1"
                  value={selected.rotation ?? 0}
                  onChange={(e) => patchSelected({ rotation: Number(e.target.value) || 0 })}
                />
              </label>
            </div>
          )}

          <div className="mt-4 border-t border-slate-100 pt-3">
            <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
              Starter designs
            </p>
            <div className="space-y-1">
              {LABEL_CANVAS_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => applyPreset(preset.id)}
                  className="w-full rounded border border-slate-200 px-2 py-1.5 text-left hover:border-sky-500 hover:bg-sky-50"
                >
                  <span className="block text-[11px] font-semibold text-slate-800">{preset.name}</span>
                  <span className="text-[10px] text-slate-500">{preset.description}</span>
                </button>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
